import { API_ENDPOINTS, withParams } from '../config/apis';
import {
  ApiError,
  NETWORK_ERROR_MESSAGE,
  apiRequest,
  getAccessToken,
  handleUnauthorizedAccess,
} from '../api/client';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';

/** 참가자 — 백엔드 ParticipantResponse (전화번호는 권한에 따라 마스킹될 수 있음) */
export type Participant = {
  id: number;
  eventId: number;
  bibNumber: number;
  chipNumber: number;
  name: string;
  gender: string | null;
  category: string;
  ageGroup: string | null;
  group: string;
  /** 'YYYY-MM-DD' */
  birth: string | null;
  phoneNumber: string | null;
};

/** 목록 응답 — 페이지네이션 메타 포함 */
export type ParticipantListResult = {
  participants: Participant[];
  totalCount: number;
  page: number;
  size: number;
};

export type ParticipantSearchParams = {
  keyword?: string;
  /** 0-based */
  page?: number;
  size?: number;
};

/** 수정 요청 본문 — 모든 필드 optional(보낸 필드만 변경, 나머지는 기존 유지). 나이그룹은 생년월일 파생이라 제외.
 * 전화번호는 빈 문자열을 보내면 지운다. 대회가 수집하지 않는 생년월일·성별은 보내도 무시된다. */
export type ParticipantUpdateBody = {
  bibNumber?: number;
  chipNumber?: number;
  name?: string;
  gender?: string;
  category?: string;
  group?: string;
  birth?: string;
  phoneNumber?: string;
};

/** 파일 삽입 실패 행 — 행 번호와 사유(필드명 목록) */
export type ParticipantImportFailure = {
  failedNum: string;
  failedReason: {
    missing: string[];
    format: string[];
    duplicate: string[];
    notFound: string[];
  };
};

/** 파일 삽입 결과 */
export type ParticipantImportResult = {
  insertedCount: number;
  failedCount: number;
  fails: ParticipantImportFailure[];
};

/** 생성 요청 본문 — 종목·그룹은 "이름"(대회 내 존재해야 함), 나이그룹은 생년월일로 자동 파생 */
export type ParticipantCreateBody = {
  bibNumber: number;
  chipNumber: number;
  name: string;
  /** 대회가 성별을 수집할 때만 필수 */
  gender?: string;
  category: string;
  group: string;
  /** 'YYYY-MM-DD' — 대회가 생년월일을 수집할 때만 필수 */
  birth?: string;
  /** 선택 입력 */
  phoneNumber?: string;
};

export const participantService = {
  search: (eventId: number, { keyword, page, size }: ParticipantSearchParams = {}) =>
    apiRequest<ParticipantListResult>(
      withParams(API_ENDPOINTS.participantList, { eventId }),
      { query: { keyword, page, size } }
    ),

  create: (eventId: number, body: ParticipantCreateBody) =>
    apiRequest<Participant>(withParams(API_ENDPOINTS.participantCreate, { eventId }), { body }),

  // 참가자 목록 CSV를 내려받는다. 바이너리 응답이라 apiRequest 대신 fetch로 blob을 받는다.
  exportCsv: async (eventId: number): Promise<Blob> => {
    const token = getAccessToken();
    const { path } = withParams(API_ENDPOINTS.participantCsvDownload, { eventId });
    let response: Response;
    try {
      response = await fetch(`${API_BASE_URL}${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch {
      throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
    }
    if (!response.ok) {
      if (response.status === 401) {
        handleUnauthorizedAccess();
      }
      throw new ApiError('참가자 목록을 내보내지 못했습니다.', response.status);
    }
    return response.blob();
  },

  // csv/xlsx 다중 삽입. 유효한 행만 저장되고 실패 행은 사유와 함께 반환된다.
  importFile: (eventId: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiRequest<ParticipantImportResult>(
      withParams(API_ENDPOINTS.participantImportFile, { eventId }),
      { body: form }
    );
  },

  update: (eventId: number, participantId: number, body: ParticipantUpdateBody) =>
    apiRequest<Participant>(
      withParams(API_ENDPOINTS.participantUpdate, { eventId, participantId }),
      { body }
    ),

  // 다건 삭제. 경기 결과가 있는 참가자는 백엔드에서 건너뛴다.
  remove: (eventId: number, participantIds: number[]) =>
    apiRequest<void>(
      withParams(API_ENDPOINTS.participantDelete, { eventId }),
      { body: { participantIds } }
    ),

  // 대회 전체 참가자 삭제. 경기 결과가 있는 참가자는 백엔드에서 건너뛴다.
  removeAll: (eventId: number) =>
    apiRequest<void>(withParams(API_ENDPOINTS.participantDeleteAll, { eventId })),
};
